import React, { useState } from 'react';
import { X, ArrowUpRight, CheckCircle2, AlertCircle, Sun } from 'lucide-react';
import { submitSolarSurvey } from '../services/firestoreService';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function SolarSurveyModal({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const [org, setOrg] = useState({ name: '', location: '', lat: '', lng: '' });
  const [contacts, setContacts] = useState([{ name: '', kdm: 'No', designation: '', mobile: '', email: '' }]);
  const [bills, setBills] = useState(MONTHS.map(m => ({ month: m, available: 'No', kwConsumed: '', kwDemand: '' })));
  const [systems, setSystems] = useState({ onGrid: 'No', offGrid: 'No', hybrid: 'No' });
  const [concerns, setConcerns] = useState('');
  const [nextSteps, setNextSteps] = useState({ cadWhen: '', cadBy: '', photoWhen: '', photoBy: '' });
  const [visit, setVisit] = useState({ date: '', faceTime: '', representative: '' });

  const addContact = () => setContacts(c => [...c, { name: '', kdm: 'No', designation: '', mobile: '', email: '' }]);
  const updateContact = (i, k, v) => setContacts(c => c.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const updateBill = (i, k, v) => setBills(b => b.map((x, j) => j === i ? { ...x, [k]: v } : x));

  const steps = [
    'Organization',
    'Contacts',
    'Electricity Bills',
    'Systems',
    'Concerns & Next Steps',
    'Visit Details',
  ];

  const submit = async () => {
    setBusy(true);
    try {
      await submitSolarSurvey({ org, contacts, bills, systems, concerns, nextSteps, visit });
      setStatus('ok');
    } catch { setStatus('err'); }
    finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="solar-survey-modal">
      <div className="absolute inset-0 bg-[var(--ink)]/60 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative bg-white rounded-3xl w-full max-w-[800px] max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--line)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[var(--orange)]/10 flex items-center justify-center">
              <Sun className="w-5 h-5 text-[var(--orange)]"/>
            </div>
            <div>
              <div className="font-display font-bold text-[18px]">Solar Site Visit Survey</div>
              <div className="text-[12px] text-[var(--text-mid)]">Step {step + 1} of {steps.length} — {steps[step]}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full border border-[var(--line)] flex items-center justify-center hover:bg-[var(--cream)]" data-testid="solar-modal-close"><X className="w-4 h-4"/></button>
        </div>

        {/* Progress */}
        <div className="px-6 pt-4">
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-[var(--orange)]' : 'bg-[var(--line)]'}`}/>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {status === 'ok' ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CheckCircle2 className="w-16 h-16 text-[var(--orange)] mb-4"/>
              <div className="font-display font-bold text-[24px] mb-2">Survey Submitted!</div>
              <p className="text-[var(--text-mid)] max-w-md">Thank you — your solar site visit survey has been submitted. Our team will review and get in touch shortly.</p>
              <button onClick={onClose} className="mt-6 px-6 py-3 rounded-full bg-[var(--orange)] text-white font-medium">Close</button>
            </div>
          ) : status === 'err' ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mb-4"/>
              <div className="font-display font-bold text-[24px] mb-2">Submission Failed</div>
              <p className="text-[var(--text-mid)]">Please try again or email solutions@tricoliteenergy.in directly.</p>
              <button onClick={() => setStatus(null)} className="mt-6 px-6 py-3 rounded-full bg-[var(--ink)] text-white font-medium">Try Again</button>
            </div>
          ) : (
            <>
              {step === 0 && (
                <div className="space-y-4">
                  <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--orange)]">Customer Organization</h4>
                  <FI label="Organization Name *" value={org.name} onChange={v => setOrg(o => ({ ...o, name: v }))} placeholder="e.g. ABC Industries Pvt Ltd"/>
                  <FI label="Site Location, State *" value={org.location} onChange={v => setOrg(o => ({ ...o, location: v }))} placeholder="e.g. Gurugram, Haryana"/>
                  <div className="grid grid-cols-2 gap-4">
                    <FI label="Latitude" value={org.lat} onChange={v => setOrg(o => ({ ...o, lat: v }))} placeholder="e.g. 28.4595"/>
                    <FI label="Longitude" value={org.lng} onChange={v => setOrg(o => ({ ...o, lng: v }))} placeholder="e.g. 77.0266"/>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--orange)]">Contacted Persons</h4>
                    <button onClick={addContact} className="text-[13px] text-[var(--orange)] font-semibold hover:underline">+ Add Person</button>
                  </div>
                  {contacts.map((c, i) => (
                    <div key={i} className="p-4 rounded-xl border border-[var(--line)] bg-[var(--cream)] space-y-3">
                      <div className="text-[11px] uppercase tracking-wider text-[var(--text-light)] font-semibold">Person {i + 1}</div>
                      <FI label="Name" value={c.name} onChange={v => updateContact(i, 'name', v)} placeholder="Full name"/>
                      <div className="grid grid-cols-2 gap-3">
                        <FI label="Designation" value={c.designation} onChange={v => updateContact(i, 'designation', v)} placeholder="e.g. Plant Head"/>
                        <div>
                          <label className="block text-[12px] uppercase tracking-wider text-[var(--text-light)] mb-1.5 font-medium">KDM?</label>
                          <select value={c.kdm} onChange={e => updateContact(i, 'kdm', e.target.value)} className="cf-input">
                            <option value="No">No</option><option value="Yes">Yes</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <FI label="Mobile No." value={c.mobile} onChange={v => updateContact(i, 'mobile', v)} placeholder="+91"/>
                        <FI label="Email ID" value={c.email} onChange={v => updateContact(i, 'email', v)} placeholder="email@company.com"/>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--orange)]">Electricity Bills Data</h4>
                  <p className="text-[13px] text-[var(--text-mid)]">Enter available monthly electricity data.</p>
                  <div className="border border-[var(--line)] rounded-xl overflow-hidden">
                    <div className="grid grid-cols-[140px_80px_1fr_1fr] bg-[var(--cream-deep)] text-[11px] uppercase tracking-wider font-semibold text-[var(--text-light)] p-3 gap-2">
                      <div>Month</div><div>Available</div><div>KW Consumed</div><div>KW Demand</div>
                    </div>
                    {bills.map((b, i) => (
                      <div key={i} className={`grid grid-cols-[140px_80px_1fr_1fr] p-2.5 gap-2 items-center ${i % 2 === 0 ? 'bg-white' : 'bg-[var(--cream)]'}`}>
                        <div className="text-[13px] font-medium">{b.month}</div>
                        <select value={b.available} onChange={e => updateBill(i, 'available', e.target.value)} className="text-[13px] border border-[var(--line)] rounded-lg px-2 py-1.5">
                          <option value="No">No</option><option value="Yes">Yes</option>
                        </select>
                        <input value={b.kwConsumed} onChange={e => updateBill(i, 'kwConsumed', e.target.value)} className="text-[13px] border border-[var(--line)] rounded-lg px-2 py-1.5" placeholder="—"/>
                        <input value={b.kwDemand} onChange={e => updateBill(i, 'kwDemand', e.target.value)} className="text-[13px] border border-[var(--line)] rounded-lg px-2 py-1.5" placeholder="—"/>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--orange)]">Systems Explained</h4>
                  <p className="text-[13px] text-[var(--text-mid)]">Which solar systems were discussed with the customer?</p>
                  {[
                    { key: 'onGrid', label: 'On Grid System', feature: 'Net Metering' },
                    { key: 'offGrid', label: 'Off Grid System', feature: 'Battery Backup Std Time' },
                    { key: 'hybrid', label: 'Hybrid System', feature: 'No Subsidy / Incentives' },
                  ].map(s => (
                    <div key={s.key} className="flex items-center justify-between p-4 rounded-xl border border-[var(--line)] bg-[var(--cream)] hover:border-[var(--orange)]/40 transition-colors cursor-pointer" onClick={() => setSystems(prev => ({ ...prev, [s.key]: prev[s.key] === 'Yes' ? 'No' : 'Yes' }))}>
                      <div>
                        <div className="font-display font-bold text-[15px]">{s.label}</div>
                        <div className="text-[12px] text-[var(--text-mid)]">{s.feature}</div>
                      </div>
                      <div className={`w-10 h-6 rounded-full flex items-center transition-colors ${systems[s.key] === 'Yes' ? 'bg-[var(--orange)] justify-end' : 'bg-[var(--line)] justify-start'}`}>
                        <div className="w-5 h-5 rounded-full bg-white shadow mx-0.5"/>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {step === 4 && (
                <div className="space-y-5">
                  <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--orange)]">Key Concerns & Next Steps</h4>
                  <div>
                    <label className="block text-[12px] uppercase tracking-wider text-[var(--text-light)] mb-1.5 font-medium">Key Customer Concerns on Solar</label>
                    <textarea value={concerns} onChange={e => setConcerns(e.target.value)} className="cf-textarea" placeholder="Describe the customer's main concerns about going solar..."/>
                  </div>
                  <h4 className="font-display font-bold text-[14px] mt-4">What will be needed next?</h4>
                  <div className="p-4 rounded-xl border border-[var(--line)] bg-[var(--cream)] space-y-3">
                    <div className="text-[13px] font-semibold">CAD File of Site Prepared / Mailed</div>
                    <div className="grid grid-cols-2 gap-3">
                      <FI label="When" value={nextSteps.cadWhen} onChange={v => setNextSteps(n => ({ ...n, cadWhen: v }))} placeholder="Timeline"/>
                      <FI label="By Whom" value={nextSteps.cadBy} onChange={v => setNextSteps(n => ({ ...n, cadBy: v }))} placeholder="Person responsible"/>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl border border-[var(--line)] bg-[var(--cream)] space-y-3">
                    <div className="text-[13px] font-semibold">Site Photograph — Before and After</div>
                    <div className="grid grid-cols-2 gap-3">
                      <FI label="When" value={nextSteps.photoWhen} onChange={v => setNextSteps(n => ({ ...n, photoWhen: v }))} placeholder="Timeline"/>
                      <FI label="By Whom" value={nextSteps.photoBy} onChange={v => setNextSteps(n => ({ ...n, photoBy: v }))} placeholder="Person responsible"/>
                    </div>
                  </div>
                </div>
              )}

              {step === 5 && (
                <div className="space-y-4">
                  <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--orange)]">Visit Details</h4>
                  <FI label="Visit Date *" value={visit.date} onChange={v => setVisit(x => ({ ...x, date: v }))} placeholder="DD/MM/YYYY" type="date"/>
                  <FI label="Face Time (Minutes)" value={visit.faceTime} onChange={v => setVisit(x => ({ ...x, faceTime: v }))} placeholder="e.g. 60"/>
                  <FI label="Visiting TES Representative *" value={visit.representative} onChange={v => setVisit(x => ({ ...x, representative: v }))} placeholder="Your name"/>
                  <div className="mt-4 p-4 rounded-xl bg-[var(--cream-deep)] border border-[var(--line)] text-[13px] text-[var(--text-mid)] italic">
                    "I hereby certify this information collected to be true, to the best of my available knowledge as on the date mentioned above."
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!status && (
          <div className="flex items-center justify-between p-6 border-t border-[var(--line)]">
            <button onClick={() => step > 0 && setStep(s => s - 1)} disabled={step === 0} className="px-5 py-2.5 rounded-full border border-[var(--line)] text-[14px] font-medium disabled:opacity-30 hover:bg-[var(--cream)] transition-colors">Back</button>
            {step < steps.length - 1 ? (
              <button onClick={() => setStep(s => s + 1)} className="px-6 py-2.5 rounded-full bg-[var(--orange)] text-white text-[14px] font-medium hover:bg-[var(--orange-soft)] transition-colors" data-testid="solar-next-btn">
                Next <ArrowUpRight className="w-3.5 h-3.5 inline ml-1"/>
              </button>
            ) : (
              <button onClick={submit} disabled={busy} className="px-6 py-2.5 rounded-full bg-[var(--orange)] text-white text-[14px] font-medium hover:bg-[var(--orange-soft)] transition-colors disabled:opacity-60" data-testid="solar-submit-btn">
                {busy ? 'Submitting…' : 'Submit Survey'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FI({ label, value, onChange, placeholder, type }) {
  return (
    <label className="block">
      <span className="block text-[12px] uppercase tracking-wider text-[var(--text-light)] mb-1.5 font-medium">{label}</span>
      <input type={type || 'text'} value={value} onChange={e => onChange(e.target.value)} className="cf-input" placeholder={placeholder}/>
    </label>
  );
}
